import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import useThemeColors from '../hooks/useThemeColors';
import PaginationBar from '../components/PaginationBar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  Search, Filter, Download, CalendarIcon, ChevronLeft, 
  ChevronRight, Eye, Loader2, FileText, X, RefreshCw, Paperclip,
  User, Building, MessageSquare, Settings, Save, CheckCircle, FileWarning,
  Upload, Trash2, FileCheck, Printer, ChevronDown, Folder, Scale, Mail, UserPlus
} from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const ESTADOS = [
  { value: 'NUEVO', label: 'Nuevo', className: 'status-nuevo' },
  { value: 'EN_PROCESO', label: 'En Proceso', className: 'status-proceso' },
  { value: 'TERMINADO', label: 'Terminado', className: 'status-terminado' },
];

const ReclamosPage = () => {
  const { api, isAdmin, hasRole, user } = useAuth();
  const themeColors = useThemeColors();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [reclamos, setReclamos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [tiposInternos, setTiposInternos] = useState([]);
  const [tipoInternoDefault, setTipoInternoDefault] = useState('SAC_VENTAS');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [lastUpdated, setLastUpdated] = useState(new Date());
  
  const [selectedReclamo, setSelectedReclamo] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [scrollToGestion, setScrollToGestion] = useState(false);
  const [reenviandoCorreo, setReenviandoCorreo] = useState(false);
  const gestionRef = useRef(null);
  
  // Estados para documentos de respuesta
  const [respuestas, setRespuestas] = useState([]);
  const [loadingRespuestas, setLoadingRespuestas] = useState(false);
  const [uploadingRespuesta, setUploadingRespuesta] = useState(false);
  const respuestaInputRef = React.useRef(null);
  
  // Estado para enviar respuesta al reclamante
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [enviandoRespuesta, setEnviandoRespuesta] = useState(false);
  // Modo del preview: 'enviar' (solo envía) | 'terminar_y_notificar' (envía y termina)
  const [previewMode, setPreviewMode] = useState('enviar');
  
  // Estado para previsualización de archivos
  const [filePreviewModal, setFilePreviewModal] = useState({ open: false, url: '', tipo: '', nombre: '' });
  
  // Ref para el intervalo de auto-refresh
  const autoRefreshInterval = useRef(null);
  
  // Estado para filtros colapsables en móvil
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Scroll automático a sección Gestión cuando se abre desde "Asignar responsable"
  useEffect(() => {
    if (scrollToGestion && detailOpen && gestionRef.current) {
      setTimeout(() => {
        gestionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setScrollToGestion(false);
      }, 300);
    }
  }, [scrollToGestion, detailOpen]);
  
  const [filters, setFilters] = useState(() => {
    const fecha_desde_param = searchParams.get('fecha_desde');
    const fecha_hasta_param = searchParams.get('fecha_hasta');
    return {
      estado: searchParams.get('estado') || '',
      busqueda: '',
      fecha_desde: fecha_desde_param ? new Date(fecha_desde_param + 'T00:00:00') : null,
      fecha_hasta: fecha_hasta_param ? new Date(fecha_hasta_param + 'T00:00:00') : null,
      pendientes_sirec: false,
      tipo_reclamo: searchParams.get('tipo_reclamo') || '',
      tipo_interno: searchParams.get('tipo_interno') || '',
      estado_resolucion: searchParams.get('estado_resolucion') || '',
      responsable_id: searchParams.get('responsable_id') || '',
    };
  });

  // Función para fetch silencioso (sin mostrar loading spinner principal)
  const fetchReclamosSilent = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 20);
      
      if (filters.estado && filters.estado !== '__all__') params.append('estado', filters.estado);
      if (filters.busqueda) params.append('busqueda', filters.busqueda);
      if (filters.fecha_desde) params.append('fecha_desde', format(filters.fecha_desde, 'yyyy-MM-dd'));
      if (filters.fecha_hasta) params.append('fecha_hasta', format(filters.fecha_hasta, 'yyyy-MM-dd'));
      if (filters.pendientes_sirec) params.append('pendientes_sirec', 'true');
      if (filters.tipo_reclamo && filters.tipo_reclamo !== '__all__') params.append('tipo_reclamo', filters.tipo_reclamo);
      if (filters.tipo_interno && filters.tipo_interno !== '__all__') params.append('tipo_interno', filters.tipo_interno);
      if (filters.estado_resolucion && filters.estado_resolucion !== '__all__') params.append('estado_resolucion', filters.estado_resolucion);
      if (filters.responsable_id && filters.responsable_id !== '__all__') params.append('responsable_id', filters.responsable_id);

      const response = await api.get(`/reclamos?${params}`);
      setReclamos(response.data.data);
      setPagination({
        page: response.data.page,
        pages: response.data.pages,
        total: response.data.total,
      });
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching reclamos:', err);
    }
  }, [api, filters]);

  const fetchReclamos = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 20);
      
      if (filters.estado && filters.estado !== '__all__') params.append('estado', filters.estado);
      if (filters.busqueda) params.append('busqueda', filters.busqueda);
      if (filters.fecha_desde) params.append('fecha_desde', format(filters.fecha_desde, 'yyyy-MM-dd'));
      if (filters.fecha_hasta) params.append('fecha_hasta', format(filters.fecha_hasta, 'yyyy-MM-dd'));
      if (filters.pendientes_sirec) params.append('pendientes_sirec', 'true');
      if (filters.tipo_reclamo && filters.tipo_reclamo !== '__all__') params.append('tipo_reclamo', filters.tipo_reclamo);
      if (filters.tipo_interno && filters.tipo_interno !== '__all__') params.append('tipo_interno', filters.tipo_interno);
      if (filters.estado_resolucion && filters.estado_resolucion !== '__all__') params.append('estado_resolucion', filters.estado_resolucion);
      if (filters.responsable_id && filters.responsable_id !== '__all__') params.append('responsable_id', filters.responsable_id);

      const response = await api.get(`/reclamos?${params}`);
      setReclamos(response.data.data);
      setPagination({
        page: response.data.page,
        pages: response.data.pages,
        total: response.data.total,
      });
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching reclamos:', err);
    } finally {
      setLoading(false);
    }
  };

  // Refresh manual con indicador visual sutil
  const handleManualRefresh = async () => {
    setRefreshing(true);
    await fetchReclamosSilent(pagination.page);
    setRefreshing(false);
  };

  const fetchUsuarios = async () => {
    try {
      const response = await api.get('/usuarios');
      setUsuarios(response.data.filter(u => u.activo && (u.rol === 'USUARIO' || u.rol === 'SUPERVISOR')));
    } catch (err) {
      console.error('Error fetching usuarios:', err);
    }
  };

  const fetchProyectos = async () => {
    try {
      const response = await api.get('/proyectos');
      setProyectos(response.data);
    } catch (err) {
      console.error('Error fetching proyectos:', err);
    }
  };

  const fetchTiposInternos = async () => {
    try {
      const response = await api.get('/config/tipos-internos');
      setTiposInternos(response.data.tipos || []);
      setTipoInternoDefault(response.data.default || 'SAC_VENTAS');
    } catch (err) {
      console.error('Error fetching tipos internos:', err);
    }
  };

  useEffect(() => {
    fetchUsuarios();
    fetchProyectos();
    fetchTiposInternos();
  }, []);

  useEffect(() => {
    fetchReclamos();
  }, [filters]);

  // Auto-refresh cada 30 segundos (solo actualiza datos, no refresca la página)
  useEffect(() => {
    // Iniciar auto-refresh
    autoRefreshInterval.current = setInterval(() => {
      // Solo hacer refresh si no hay un modal abierto
      if (!detailOpen) {
        fetchReclamosSilent(pagination.page);
      }
    }, 30000); // 30 segundos

    // Limpiar intervalo al desmontar
    return () => {
      if (autoRefreshInterval.current) {
        clearInterval(autoRefreshInterval.current);
      }
    };
  }, [detailOpen, pagination.page, fetchReclamosSilent]);

  const openDetail = async (reclamo) => {
    try {
      const response = await api.get(`/reclamos/${reclamo.id || reclamo}`);
      setSelectedReclamo(response.data);
      setDetailOpen(true);
      fetchRespuestas(response.data.id);
    } catch (err) {
      console.error('Error fetching reclamo detail:', err);
    }
  };

  // Deep-link: abrir detalle si viene ?detail=reclamoId
  useEffect(() => {
    const detailId = searchParams.get('detail');
    if (detailId) {
      openDetail(detailId);
      searchParams.delete('detail');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  const fetchRespuestas = async (reclamoId) => {
    setLoadingRespuestas(true);
    try {
      const response = await api.get(`/reclamos/${reclamoId}/respuestas`);
      setRespuestas(response.data || []);
    } catch (err) {
      console.error('Error fetching respuestas:', err);
      setRespuestas([]);
    } finally {
      setLoadingRespuestas(false);
    }
  };

  const PREVIEWABLE_EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg', 'mp4', 'mov'];
  
  const canPreviewFile = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase() || '';
    return PREVIEWABLE_EXTENSIONS.includes(ext);
  };

  const getFileExtension = (filename) => (filename?.split('.').pop()?.toLowerCase() || '');

  const handleFilePreview = async (fileUrl, filename) => {
    try {
      const token = localStorage.getItem('ldrv_token');
      const url = fileUrl.startsWith('http') ? fileUrl : `${process.env.REACT_APP_BACKEND_URL}${fileUrl}`;
      const response = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        toast.error('Archivo no disponible para previsualización');
        return;
      }
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const ext = getFileExtension(filename);
      setFilePreviewModal({ open: true, url: blobUrl, tipo: ext, nombre: filename });
    } catch {
      toast.error('Error al previsualizar el archivo');
    }
  };

  const handleUploadRespuesta = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedReclamo) return;

    // Validar tipo de archivo
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Solo se permiten archivos PDF o Word (.doc, .docx)');
      return;
    }

    // Validar tamaño (5MB máx)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('El archivo no puede superar 5MB');
      return;
    }

    setUploadingRespuesta(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await api.post(`/reclamos/${selectedReclamo.id}/respuestas`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Documento de respuesta subido correctamente');
      fetchRespuestas(selectedReclamo.id);
    } catch (err) {
      console.error('Error uploading respuesta:', err);
      toast.error('Error al subir el documento');
    } finally {
      setUploadingRespuesta(false);
      if (respuestaInputRef.current) {
        respuestaInputRef.current.value = '';
      }
    }
  };

  const handleDeleteRespuesta = async (respuestaId) => {
    if (!selectedReclamo) return;
    
    try {
      await api.delete(`/reclamos/${selectedReclamo.id}/respuestas/${respuestaId}`);
      toast.success('Documento eliminado correctamente');
      fetchRespuestas(selectedReclamo.id);
    } catch (err) {
      console.error('Error deleting respuesta:', err);
      toast.error('Error al eliminar el documento');
    }
  };

  const handlePrintReclamo = (reclamo) => {
    const proyecto = reclamo.proyectos || {};
    const empresa = proyecto.empresas || {};
    
    // Formatear fecha
    const fechaReclamo = new Date(reclamo.created_at);
    const dia = fechaReclamo.getDate().toString().padStart(2, '0');
    const mes = (fechaReclamo.getMonth() + 1).toString().padStart(2, '0');
    const anio = fechaReclamo.getFullYear();
    const fechaFormateada = `${dia}/${mes}/${anio}`;
    
    // Formatear fecha de comunicación de respuesta
    let fechaComunicacion = '';
    if (reclamo.fecha_comunicacion_respuesta) {
      const fechaComm = new Date(reclamo.fecha_comunicacion_respuesta);
      fechaComunicacion = `${fechaComm.getDate().toString().padStart(2, '0')}/${(fechaComm.getMonth() + 1).toString().padStart(2, '0')}/${fechaComm.getFullYear()}`;
    }
    
    // Checkbox marcado
    const checkMark = '☑';
    const checkEmpty = '☐';
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Hoja de Reclamación - ${reclamo.folio}</title>
        <style>
          @page { size: A4; margin: 10mm; }
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: Arial, sans-serif; font-size: 10pt; line-height: 1.4; color: #000; }
          .container { width: 100%; max-width: 190mm; margin: 0 auto; border: 2px solid #000; }
          .header { display: flex; align-items: center; justify-content: space-between; padding: 10px 15px; border-bottom: 2px solid #000; }
          .header-left { display: flex; align-items: center; gap: 15px; }
          .logo { height: 50px; max-width: 100px; object-fit: contain; }
          .header-title { font-size: 14pt; font-weight: bold; }
          .header-right { text-align: right; }
          .header-right .label { font-size: 10pt; }
          .header-right .folio { font-size: 16pt; font-weight: bold; }
          
          .info-section { padding: 10px 15px; border-bottom: 1px solid #000; }
          .info-row { margin: 3px 0; }
          .info-label { font-weight: bold; }
          
          .section { border-bottom: 1px solid #000; }
          .section-title { background: #f0f0f0; padding: 8px 15px; font-weight: bold; font-size: 10pt; border-bottom: 1px solid #000; }
          .section-content { padding: 10px 15px; }
          
          .two-col { display: flex; }
          .col-left { flex: 1; padding-right: 10px; border-right: 1px solid #000; }
          .col-right { width: 150px; padding-left: 10px; display: flex; flex-direction: column; justify-content: flex-end; align-items: center; padding-bottom: 5px; }
          
          .section-3-content .two-col { min-height: 80px; }
          .section-4-content .two-col { min-height: 80px; }
          
          .field { margin: 5px 0; }
          .field-label { font-weight: bold; }
          .field-value { margin-top: 2px; }
          
          .checkbox-group { margin: 5px 0; }
          .checkbox-item { margin: 3px 0; font-size: 10pt; }
          
          .signature-area { text-align: center; }
          .signature-line { width: 120px; border-bottom: 1px solid #000; margin: 0 auto 5px; }
          .signature-label { font-size: 9pt; }
          
          .section-3-content { min-height: 100px; }
          .section-4-content { min-height: 80px; }
          
          .footnotes { padding: 10px 15px; font-size: 8pt; border-bottom: 1px solid #000; }
          .footnotes p { margin: 3px 0; }
          
          .footer { padding: 10px 15px; text-align: center; font-size: 9pt; }
          .footer-title { font-weight: bold; margin-bottom: 8px; }
          .footer-text { margin: 3px 0; }
          
          .whitespace-pre { white-space: pre-wrap; }
        </style>
      </head>
      <body>
        <div class="container">
          <!-- Header -->
          <div class="header">
            <div class="header-left">
              ${empresa.logo_url ? `<img src="${empresa.logo_url}" class="logo" alt="Logo">` : ''}
              <span class="header-title">LIBRO DE RECLAMACIONES</span>
            </div>
            <div class="header-right">
              <div class="label">HOJA DE RECLAMACIÓN N°</div>
              <div class="folio">${reclamo.folio}</div>
            </div>
          </div>
          
          <!-- Info del Proveedor -->
          <div class="info-section">
            <div class="info-row"><span class="info-label">FECHA:</span> ${fechaFormateada}</div>
            <div class="info-row"><span class="info-label">PROVEEDOR:</span> ${proyecto.razon_social || ''}</div>
            <div class="info-row"><span class="info-label">RUC:</span> ${proyecto.ruc || ''}</div>
            <div class="info-row"><span class="info-label">DOMICILIO:</span> ${proyecto.direccion || ''}</div>
          </div>
          
          <!-- Sección 1 -->
          <div class="section">
            <div class="section-title">1. IDENTIFICACIÓN DEL CONSUMIDOR RECLAMANTE</div>
            <div class="section-content">
              <div class="field"><span class="field-label">NOMBRE:</span> ${reclamo.nombre_completo}</div>
              <div class="field"><span class="field-label">DNI / CE:</span> ${reclamo.numero_documento}</div>
              <div class="field"><span class="field-label">DOMICILIO:</span> ${reclamo.domicilio || ''}</div>
              <div class="field">
                <span class="field-label">TELÉFONO:</span> ${reclamo.telefono || ''}
                &nbsp;&nbsp;&nbsp;&nbsp;
                <span class="field-label">E-MAIL:</span> ${reclamo.correo_electronico}
              </div>
              <div class="field">
                <span class="field-label">SI ES MENOR DE EDAD, NOMBRE DEL PADRE, MADRE O APODERADO:</span><br>
                ${reclamo.es_menor_edad ? (reclamo.nombre_apoderado || '') : ''}
              </div>
            </div>
          </div>
          
          <!-- Sección 2 -->
          <div class="section">
            <div class="section-title">2. IDENTIFICACIÓN DEL BIEN CONTRATADO</div>
            <div class="section-content">
              <div class="two-col" style="border-bottom: 1px solid #ccc; padding-bottom: 8px; margin-bottom: 8px;">
                <div class="col-left" style="border: none;">
                  <div class="checkbox-group">
                    <div class="checkbox-item">${reclamo.tipo_bien === 'PRODUCTO' ? checkMark : checkEmpty} PRODUCTO</div>
                    <div class="checkbox-item">${reclamo.tipo_bien === 'SERVICIO' ? checkMark : checkEmpty} SERVICIO</div>
                  </div>
                </div>
                <div class="col-right" style="text-align: left; align-items: flex-start;">
                  <span class="field-label">MONTO RECLAMADO:</span>
                  S/ ${parseFloat(reclamo.monto_reclamado || 0).toFixed(2)}
                </div>
              </div>
              <div class="field">
                <span class="field-label">DESCRIPCIÓN:</span>
                <div class="field-value whitespace-pre">${reclamo.descripcion_bien || ''}</div>
              </div>
            </div>
          </div>
          
          <!-- Sección 3 -->
          <div class="section">
            <div class="section-title">3. DETALLE DE LA RECLAMACIÓN Y PEDIDO DEL CONSUMIDOR</div>
            <div class="section-content section-3-content">
              <div class="two-col" style="margin-bottom: 10px;">
                <div class="col-left">
                  <div class="field">
                    <span class="field-label">DETALLE:</span>
                    <div class="field-value whitespace-pre">${reclamo.descripcion_reclamo || ''}</div>
                  </div>
                </div>
                <div class="col-right">
                  <div class="checkbox-group">
                    <div class="checkbox-item">${reclamo.tipo_reclamo === 'RECLAMO' ? checkMark : checkEmpty} RECLAMO¹</div>
                    <div class="checkbox-item">${reclamo.tipo_reclamo === 'QUEJA' ? checkMark : checkEmpty} QUEJA²</div>
                  </div>
                </div>
              </div>
              <div class="two-col">
                <div class="col-left">
                  <div class="field">
                    <span class="field-label">PEDIDO:</span>
                    <div class="field-value whitespace-pre">${reclamo.pedido_consumidor || ''}</div>
                  </div>
                </div>
                <div class="col-right">
                  <div class="signature-area">
                    <div class="signature-line"></div>
                    <div class="signature-label">FIRMA DEL CONSUMIDOR</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Sección 4 -->
          <div class="section">
            <div class="section-title">4. OBSERVACIONES Y ACCIONES ADOPTADAS POR EL PROVEEDOR</div>
            <div class="section-content section-4-content">
              <div class="two-col">
                <div class="col-left">
                  <div class="field">
                    <span class="field-label">FECHA DE COMUNICACIÓN DE LA RESPUESTA:</span>
                    <div class="field-value">${fechaComunicacion || '_____________________'}</div>
                  </div>
                  <div class="field" style="margin-top: 10px;">
                    <div class="field-value whitespace-pre">${reclamo.observaciones || ''}</div>
                  </div>
                </div>
                <div class="col-right">
                  <div class="signature-area">
                    <div class="signature-line"></div>
                    <div class="signature-label">FIRMA DEL PROVEEDOR</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Footnotes -->
          <div class="footnotes">
            <p>¹ RECLAMO: Disconformidad relacionada a los productos o servicios.</p>
            <p>² QUEJA: Disconformidad no relacionada a los productos o servicios; o, malestar o descontento respecto a la atención al público.</p>
          </div>
          
          <!-- Footer -->
          <div class="footer">
            <div class="footer-title">HOJA DE RECLAMACIÓN VIRTUAL</div>
            <p class="footer-text">* La formulación del reclamo no impide acudir a otras vías de solución de controversias ni es requisito previo para interponer una denuncia ante el INDECOPI.</p>
            <p class="footer-text">* El proveedor debe dar respuesta al reclamo o queja en un plazo no mayor a quince (15) días hábiles, el cual es improrrogable.</p>
          </div>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const updateReclamo = async (field, value) => {
    if (!selectedReclamo) return;
    setUpdating(true);
    
    try {
      const updateData = { [field]: value };
      const response = await api.put(`/reclamos/${selectedReclamo.id}`, updateData);
      setSelectedReclamo({ ...selectedReclamo, ...response.data });
      fetchReclamos(pagination.page);
    } catch (err) {
      console.error('Error updating reclamo:', err);
    } finally {
      setUpdating(false);
    }
  };

  const getEstadoBadge = (estado, opts = {}) => {
    const estadoInfo = ESTADOS.find(e => e.value === estado);
    const { notificado = false } = opts;
    return (
      <div className="inline-flex items-center gap-1.5 flex-wrap" data-testid="estado-cell">
        <Badge className={`status-badge ${estadoInfo?.className || ''}`}>
          {estadoInfo?.label || estado}
        </Badge>
        {notificado && estado === 'TERMINADO' && (
          <Badge
            variant="outline"
            className="text-emerald-700 border-emerald-300 bg-emerald-50 hover:bg-emerald-100 p-0 h-5 w-5 flex items-center justify-center"
            title="Notificado"
            aria-label="Notificado"
            data-testid="badge-notificado"
          >
            <Mail className="w-3 h-3" />
          </Badge>
        )}
      </div>
    );
  };

  const getResolucionBadge = (reclamo) => {
    const res = reclamo.resoluciones;
    const reclamoId = reclamo.id;
    const handleClick = (e) => {
      e.stopPropagation();
      navigate(`/admin/reclamos/${reclamoId}/resolucion`);
    };
    // Si no tiene responsable asignado, mostrar "Asignar responsable"
    if (!reclamo.responsable_id) {
      const handleAsignar = (e) => {
        e.stopPropagation();
        setSelectedReclamo(reclamo);
        setScrollToGestion(true);
        setDetailOpen(true);
      };
      return (
        <Badge 
          variant="outline" 
          className="text-amber-700 border-amber-400 bg-amber-50 cursor-pointer hover:bg-amber-100 gap-1" 
          onClick={handleAsignar} 
          data-testid={`asignar-responsable-${reclamoId}`}
        >
          <UserPlus className="w-3 h-3" />
          Asignar responsable
        </Badge>
      );
    }
    if (!res) {
      return (
        <Badge variant="outline" className="text-blue-600 border-blue-300 cursor-pointer hover:bg-blue-50" onClick={handleClick} data-testid={`resolucion-link-${reclamoId}`}>
          Sin resolución
        </Badge>
      );
    }
    const estado = Array.isArray(res) ? res[0]?.estado : res.estado;
    if (!estado) {
      return (
        <Badge variant="outline" className="text-blue-600 border-blue-300 cursor-pointer hover:bg-blue-50" onClick={handleClick} data-testid={`resolucion-link-${reclamoId}`}>
          Sin resolución
        </Badge>
      );
    }
    const config = {
      'EN_PROCESO': { label: 'En Proceso', className: 'bg-blue-100 text-blue-800' },
      'PENDIENTE_APROBACION': { label: 'Pendiente Aprobación', className: 'bg-amber-100 text-amber-800' },
      'APROBADO': { label: 'Aprobado', className: 'bg-green-100 text-green-800' },
      'POSPUESTO': { label: 'Pospuesto', className: 'bg-red-100 text-red-800' },
    };
    const c = config[estado] || { label: estado, className: 'bg-gray-100 text-gray-800' };
    return <Badge className={`${c.className} cursor-pointer hover:opacity-80`} onClick={handleClick} data-testid={`resolucion-link-${reclamoId}`}>{c.label}</Badge>;
  };

  const clearFilters = () => {
    setFilters({
      estado: '',
      busqueda: '',
      fecha_desde: null,
      fecha_hasta: null,
      pendientes_sirec: false,
      tipo_reclamo: '',
      tipo_interno: '',
      estado_resolucion: '',
      responsable_id: '',
    });
    setSearchParams({});
  };
  
  // Función para exportar trama SIREC
  const [exportingSirec, setExportingSirec] = useState(false);
  
  const handleExportarSirec = async () => {
    setExportingSirec(true);
    try {
      const response = await api.get('/reclamos/exportar-sirec', {
        responseType: 'blob'
      });
      
      // Crear blob y descargar
      const blob = new Blob([response.data], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const ahora = new Date();
      const nombreArchivo = `trama_sirec_${ahora.getFullYear()}${String(ahora.getMonth() + 1).padStart(2, '0')}${String(ahora.getDate()).padStart(2, '0')}_${String(ahora.getHours()).padStart(2, '0')}${String(ahora.getMinutes()).padStart(2, '0')}${String(ahora.getSeconds()).padStart(2, '0')}.txt`;
      link.download = nombreArchivo;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Archivo de trama SIREC generado correctamente');
    } catch (err) {
      console.error('Error exportando SIREC:', err);
      toast.error(err.response?.data?.detail || 'Error al exportar trama SIREC');
    } finally {
      setExportingSirec(false);
    }
  };

  // Función para exportar reclamos a Excel
  const [exportingExcel, setExportingExcel] = useState(false);

  const handleExportarExcel = async () => {
    setExportingExcel(true);
    try {
      const params = new URLSearchParams();
      if (filters.estado) params.append('estado', filters.estado);
      if (filters.proyecto) params.append('proyecto_id', filters.proyecto);
      if (filters.busqueda) params.append('busqueda', filters.busqueda);
      if (filters.fechaDesde) params.append('fecha_desde', filters.fechaDesde);
      if (filters.fechaHasta) params.append('fecha_hasta', filters.fechaHasta);
      if (filters.pendientes_sirec) params.append('pendientes_sirec', 'true');

      const response = await api.get(`/reclamos/exportar-excel?${params.toString()}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const ahora = new Date();
      link.download = `reclamos_${ahora.getFullYear()}${String(ahora.getMonth() + 1).padStart(2, '0')}${String(ahora.getDate()).padStart(2, '0')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Archivo Excel generado correctamente');
    } catch (err) {
      console.error('Error exportando Excel:', err);
      toast.error(err.response?.data?.detail || 'Error al exportar a Excel');
    } finally {
      setExportingExcel(false);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="reclamos-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Reclamos</h1>
          <p className="text-slate-500 text-sm mt-1">
            {pagination.total} reclamos encontrados
            <span className="mx-2">•</span>
            <span className="text-xs">
              Última actualización: {format(lastUpdated, 'HH:mm:ss', { locale: es })}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={handleManualRefresh}
                  disabled={refreshing}
                  data-testid="refresh-btn"
                  className="relative"
                >
                  <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                  {refreshing && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Actualizar listado</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Button 
            variant="outline" 
            data-testid="exportar-btn"
            onClick={handleExportarExcel}
            disabled={exportingExcel || reclamos.length === 0}
          >
            {exportingExcel ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            {exportingExcel ? 'Exportando...' : 'Exportar'}
          </Button>
          {/* Botón Exportar Trama SIREC - visible solo cuando el filtro está activo */}
          {filters.pendientes_sirec && (
            <Button 
              variant="outline" 
              data-testid="exportar-sirec-btn"
              onClick={handleExportarSirec}
              disabled={exportingSirec || reclamos.length === 0}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              {exportingSirec ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileWarning className="w-4 h-4 mr-2" />
              )}
              Exportar trama SIREC
            </Button>
          )}
        </div>
      </div>

      {/* Filters - Responsive */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          {/* Mobile: Search + Collapsible filters */}
          <div className="space-y-4">
            {/* Search - Always visible */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar por folio, nombre, documento, correo o proyecto"
                  data-testid="busqueda-input"
                  className="pl-10"
                  value={filters.busqueda}
                  onChange={(e) => setFilters({ ...filters, busqueda: e.target.value })}
                />
              </div>
              
              {/* Filter toggle button - Mobile only */}
              <Button 
                variant="outline" 
                className="md:hidden"
                onClick={() => setFiltersOpen(!filtersOpen)}
              >
                <Filter className="w-4 h-4 mr-1" />
                <ChevronDown className={`w-4 h-4 transition-transform ${filtersOpen ? 'rotate-180' : ''}`} />
              </Button>
            </div>

            {/* Collapsible filters for mobile, always visible on desktop */}
            <div className={`${filtersOpen ? 'block' : 'hidden'} md:block`}>
              <div className="flex flex-wrap items-center gap-3">
                {/* Estado */}
                <Select
                  value={filters.estado}
                  onValueChange={(value) => setFilters({ ...filters, estado: value })}
                >
                  <SelectTrigger className="w-full sm:w-[150px]" data-testid="estado-filter">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos</SelectItem>
                    {ESTADOS.map((e) => (
                      <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Switch Pendientes SIREC */}
                <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-slate-200 bg-white">
                  <Switch
                    id="pendientes-sirec"
                    data-testid="pendientes-sirec-switch"
                    checked={filters.pendientes_sirec}
                    onCheckedChange={(checked) => setFilters({ ...filters, pendientes_sirec: checked })}
                  />
                  <label 
                    htmlFor="pendientes-sirec" 
                    className="text-sm font-medium text-slate-700 cursor-pointer whitespace-nowrap"
                  >
                    Pendientes SIREC
                  </label>
                </div>

                {/* Tipo Reclamo */}
                <Select value={filters.tipo_reclamo} onValueChange={(v) => setFilters({ ...filters, tipo_reclamo: v })}>
                  <SelectTrigger className="w-full sm:w-[140px]" data-testid="tipo-reclamo-filter">
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos</SelectItem>
                    <SelectItem value="RECLAMO">Reclamo</SelectItem>
                    <SelectItem value="QUEJA">Queja</SelectItem>
                  </SelectContent>
                </Select>

                {/* Tipo Interno */}
                <Select value={filters.tipo_interno} onValueChange={(v) => setFilters({ ...filters, tipo_interno: v })}>
                  <SelectTrigger className="w-full sm:w-[160px]" data-testid="tipo-interno-filter">
                    <SelectValue placeholder="Tipo Interno" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos</SelectItem>
                    {tiposInternos.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>

                {/* Estado Resolución */}
                <Select value={filters.estado_resolucion} onValueChange={(v) => setFilters({ ...filters, estado_resolucion: v })}>
                  <SelectTrigger className="w-full sm:w-[170px]" data-testid="estado-resolucion-filter">
                    <SelectValue placeholder="Resolucion" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todas</SelectItem>
                    <SelectItem value="sin_resolucion">Sin Resolucion</SelectItem>
                    <SelectItem value="borrador">En Proceso</SelectItem>
                    <SelectItem value="pendiente_aprobacion">Pend. Aprobacion</SelectItem>
                    <SelectItem value="aprobada">Aprobada</SelectItem>
                  </SelectContent>
                </Select>

                {/* Responsable - visible solo para ADMIN, SUPERVISOR, LEGAL */}
                {(hasRole('ADMINISTRADOR', 'SUPERADMIN', 'SUPERVISOR', 'LEGAL')) && (
                  <Select value={filters.responsable_id} onValueChange={(v) => setFilters({ ...filters, responsable_id: v })}>
                    <SelectTrigger className="w-full sm:w-[170px]" data-testid="responsable-filter">
                      <SelectValue placeholder="Responsable" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Todos</SelectItem>
                      {usuarios.filter(u => u.rol === 'USUARIO' || u.rol === 'SUPERVISOR').map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {/* Dates */}
                <div className="flex gap-2 w-full sm:w-auto">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="flex-1 sm:flex-none sm:w-[130px]">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {filters.fecha_desde ? format(filters.fecha_desde, 'dd/MM/yy') : 'Desde'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={filters.fecha_desde}
                        onSelect={(date) => setFilters({ ...filters, fecha_desde: date })}
                        locale={es}
                      />
                    </PopoverContent>
                  </Popover>

                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="flex-1 sm:flex-none sm:w-[130px]">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {filters.fecha_hasta ? format(filters.fecha_hasta, 'dd/MM/yy') : 'Hasta'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={filters.fecha_hasta}
                        onSelect={(date) => setFilters({ ...filters, fecha_hasta: date })}
                        locale={es}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {(filters.estado || filters.pendientes_sirec || filters.busqueda || filters.tipo_reclamo || filters.tipo_interno || filters.estado_resolucion || filters.responsable_id) && (
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    <X className="w-4 h-4 mr-1" />
                    Limpiar
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data View - Responsive */}
      {loading ? (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          </CardContent>
        </Card>
      ) : reclamos.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <FileText className="w-12 h-12 mb-4 text-slate-300" />
              <p>No se encontraron reclamos</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Vista móvil - Cards */}
          <div className="md:hidden space-y-3">
            {reclamos.map((reclamo) => (
              <Card 
                key={reclamo.id}
                className="border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all cursor-pointer"
                onClick={() => openDetail(reclamo)}
                data-testid={`reclamo-card-${reclamo.id}`}
              >
                <CardContent className="p-4">
                  {/* Header: Folio + Estado */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <p className="font-mono font-bold text-slate-900">{reclamo.folio}</p>
                      <p className="text-sm text-slate-600 truncate">{reclamo.proyectos?.nombre}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {getEstadoBadge(reclamo.estado, { notificado: !!reclamo.respuesta_enviada_reclamante })}
                      {getResolucionBadge(reclamo)}
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </div>
                  </div>
                  
                  {/* Datos principales */}
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Reclamante:</span>
                      <span className="text-slate-800 text-right truncate max-w-[180px]">{reclamo.nombre_completo}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Fecha:</span>
                      <span className="text-slate-800">{format(new Date(reclamo.created_at), 'dd/MM/yyyy HH:mm')}</span>
                    </div>
                    {reclamo.usuarios?.nombre && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Responsable:</span>
                        <span className="text-slate-800">{reclamo.usuarios.nombre}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Vista desktop - Tabla */}
          <Card className="border-slate-200 hidden md:block">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="table-header">
                      <TableHead>Folio</TableHead>
                      <TableHead>Proyecto</TableHead>
                      <TableHead className="hidden lg:table-cell">Reclamante</TableHead>
                      <TableHead className="hidden xl:table-cell">RUC</TableHead>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="hidden xl:table-cell">Responsable</TableHead>
                      <TableHead>Resolución</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reclamos.map((reclamo) => (
                      <TableRow 
                        key={reclamo.id} 
                        className="table-row cursor-pointer"
                        onClick={() => openDetail(reclamo)}
                        data-testid={`reclamo-row-${reclamo.id}`}
                      >
                        <TableCell className="font-mono font-medium text-sm">
                          {reclamo.folio}
                        </TableCell>
                        <TableCell className="max-w-[150px] truncate">
                          {reclamo.proyectos?.nombre}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell max-w-[150px] truncate">
                          {reclamo.nombre_completo}
                        </TableCell>
                        <TableCell className="hidden xl:table-cell font-mono text-sm">
                          {reclamo.proyectos?.ruc}
                        </TableCell>
                        <TableCell className="text-sm">
                          {format(new Date(reclamo.created_at), 'dd/MM/yyyy HH:mm')}
                        </TableCell>
                        <TableCell>
                          {getEstadoBadge(reclamo.estado, { notificado: !!reclamo.respuesta_enviada_reclamante })}
                        </TableCell>
                        <TableCell className="hidden xl:table-cell">
                          {reclamo.usuarios?.nombre || '-'}
                        </TableCell>
                        <TableCell>
                          {getResolucionBadge(reclamo)}
                        </TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDetail(reclamo);
                            }}
                            data-testid={`ver-reclamo-${reclamo.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Pagination - Ambas vistas */}
          <PaginationBar pagination={pagination} onPageChange={fetchReclamos} themeColors={themeColors} />
        </>
      )}

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent
          className="max-w-[min(1024px,95vw)] max-h-[90vh] overflow-hidden flex flex-col"
          aria-describedby="reclamo-detail-description"
          style={{ '--rc-primary': themeColors.primario || '#475569' }}
        >
          {selectedReclamo && (
            <>
              <DialogHeader className="border-b pb-3.5 flex-shrink-0 bg-white sticky top-0 z-10 pr-10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3.5">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ background: `color-mix(in srgb, ${themeColors.primario || '#475569'} 12%, transparent)` }}
                    >
                      <FileText className="w-5 h-5" style={{ color: themeColors.primario || '#475569' }} />
                    </div>
                    <div>
                      <DialogTitle className="flex items-center gap-3 text-lg">
                        <span className="font-mono font-bold">{selectedReclamo.folio}</span>
                      </DialogTitle>
                      <p id="reclamo-detail-description" className="text-xs text-slate-500 mt-0.5">
                        Registrado el {format(new Date(selectedReclamo.created_at), "dd 'de' MMMM 'de' yyyy 'a las' HH:mm", { locale: es })}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setDetailOpen(false);
                          navigate(`/admin/reclamos/${selectedReclamo.id}/resolucion`);
                        }}
                        data-testid="btn-gestionar-resolucion"
                      >
                        <Scale className="w-4 h-4 mr-1" />
                        Resolución
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePrintReclamo(selectedReclamo)}
                        data-testid="btn-imprimir-reclamo"
                      >
                        <Printer className="w-4 h-4 mr-1" />
                        Imprimir
                      </Button>
                      {getEstadoBadge(selectedReclamo.estado, { notificado: !!selectedReclamo.respuesta_enviada_reclamante })}
                    </div>
                    {selectedReclamo.usuarios && (
                      <span className="text-xs text-slate-500">
                        Asignado a: <span className="font-medium">{selectedReclamo.usuarios.nombre}</span>
                      </span>
                    )}
                  </div>
                </div>
              </DialogHeader>

              <div className="flex flex-col gap-5 py-5 pr-3 overflow-y-auto flex-1 [&>*]:flex-none [&>span>*]:flex-none">
                {/* Info del Reclamante */}
                <div className="rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                  <div
                    className="px-3.5 py-2.5 border-b border-slate-200 border-l-[3px]"
                    style={{
                      background: `linear-gradient(to right, color-mix(in srgb, ${themeColors.primario || '#475569'} 10%, transparent), color-mix(in srgb, ${themeColors.primario || '#475569'} 3%, transparent))`,
                      borderLeftColor: themeColors.primario || '#475569',
                    }}
                  >
                    <h3 className="font-heading font-bold text-slate-900 text-sm flex items-center gap-2">
                      <User className="w-4 h-4" style={{ color: themeColors.primario || '#475569' }} />
                      Datos del Reclamante
                    </h3>
                  </div>
                  <div className="p-3.5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3.5">
                      <div className="space-y-1">
                        <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Nombre Completo</p>
                        <p className="text-sm font-semibold text-slate-800">{selectedReclamo.nombre_completo}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Documento</p>
                        <p className="text-sm font-mono text-slate-800">{selectedReclamo.tipo_documento} {selectedReclamo.numero_documento}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Correo Electrónico</p>
                        <p className="text-sm text-slate-800 break-all">{selectedReclamo.correo_electronico}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Teléfono</p>
                        <p className="text-sm text-slate-800">{selectedReclamo.telefono || '—'}</p>
                      </div>
                    </div>
                    {selectedReclamo.domicilio && (
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Domicilio</p>
                        <p className="text-sm text-slate-800 mt-1">{selectedReclamo.domicilio}</p>
                      </div>
                    )}
                    {selectedReclamo.es_menor_edad && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3.5">
                        <div className="space-y-1">
                          <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Es menor de edad</p>
                          <p className="text-sm font-semibold text-slate-800">Sí</p>
                        </div>
                            <div className="space-y-1">
                              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Nombre del Apoderado</p>
                              <p className="text-sm text-slate-800">{selectedReclamo.nombre_apoderado || '—'}</p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Correo del Apoderado</p>
                              <p className="text-sm text-slate-800 break-all">{selectedReclamo.correo_apoderado || '—'}</p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Teléfono del Apoderado</p>
                              <p className="text-sm text-slate-800">{selectedReclamo.telefono_apoderado || '—'}</p>
                            </div>
                            {selectedReclamo.domicilio_apoderado && (
                              <div className="space-y-1 col-span-2">
                                <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Domicilio del Apoderado</p>
                                <p className="text-sm text-slate-800">{selectedReclamo.domicilio_apoderado}</p>
                              </div>
                            )}
                      </div>
                    </div>
                    )}
                  </div>
                </div>

                {/* Info del Proyecto */}
                <div className="rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                  <div
                    className="px-3.5 py-2.5 border-b border-slate-200 border-l-[3px]"
                    style={{
                      background: `linear-gradient(to right, color-mix(in srgb, ${themeColors.primario || '#475569'} 10%, transparent), color-mix(in srgb, ${themeColors.primario || '#475569'} 3%, transparent))`,
                      borderLeftColor: themeColors.primario || '#475569',
                    }}
                  >
                    <h3 className="font-heading font-bold text-slate-900 text-sm flex items-center gap-2">
                      <Building className="w-4 h-4" style={{ color: themeColors.primario || '#475569' }} />
                      Información del Proyecto
                    </h3>
                  </div>
                  <div className="p-3.5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3.5">
                      <div className="space-y-1">
                        <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Proyecto</p>
                        <p className="text-sm font-semibold text-slate-800">{selectedReclamo.proyectos?.nombre}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">RUC</p>
                        <p className="text-sm font-mono text-slate-800">{selectedReclamo.proyectos?.ruc}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Razón Social</p>
                        <p className="text-sm text-slate-800">{selectedReclamo.proyectos?.razon_social}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Detalle del Reclamo */}
                <div className="rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                  <div
                    className="px-3.5 py-2.5 border-b border-slate-200 border-l-[3px]"
                    style={{
                      background: `linear-gradient(to right, color-mix(in srgb, ${themeColors.primario || '#475569'} 10%, transparent), color-mix(in srgb, ${themeColors.primario || '#475569'} 3%, transparent))`,
                      borderLeftColor: themeColors.primario || '#475569',
                    }}
                  >
                    <h3 className="font-heading font-bold text-slate-900 text-sm flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" style={{ color: themeColors.primario || '#475569' }} />
                      Detalle del Reclamo
                    </h3>
                  </div>
                  <div className="p-4 space-y-4">
                    <div className="flex flex-wrap gap-2">
                      <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">{selectedReclamo.tipo_bien}</Badge>
                      <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100">{selectedReclamo.tipo_reclamo}</Badge>
                      {selectedReclamo.monto_reclamado && (
                        <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                          S/ {parseFloat(selectedReclamo.monto_reclamado).toFixed(2)}
                        </Badge>
                      )}
                    </div>
                    
                    {selectedReclamo.descripcion_bien && (
                      <div className="space-y-2 pt-4 mt-4 border-t border-slate-100">
                        <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Descripción del Bien</p>
                        <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                          <p className="text-sm text-slate-700">{selectedReclamo.descripcion_bien}</p>
                        </div>
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">Descripción del Reclamo</p>
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedReclamo.descripcion_reclamo}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">Pedido del Consumidor</p>
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedReclamo.pedido_consumidor}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Archivos Adjuntos */}
                {selectedReclamo.archivos && selectedReclamo.archivos.length > 0 && (
                  <div className="rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                    <div
                      className="px-3.5 py-2.5 border-b border-slate-200 border-l-[3px]"
                      style={{
                        background: `linear-gradient(to right, color-mix(in srgb, ${themeColors.primario || '#475569'} 10%, transparent), color-mix(in srgb, ${themeColors.primario || '#475569'} 3%, transparent))`,
                        borderLeftColor: themeColors.primario || '#475569',
                      }}
                    >
                      <h3 className="font-heading font-bold text-slate-900 text-sm flex items-center gap-2">
                        <Paperclip className="w-4 h-4" style={{ color: themeColors.primario || '#475569' }} />
                        Archivos Adjuntos
                        <Badge variant="secondary" className="ml-2">{selectedReclamo.archivos.length}</Badge>
                      </h3>
                    </div>
                    <div className="p-3.5">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {selectedReclamo.archivos.map((archivo) => {
                          const isImage = archivo.tipo_mime?.startsWith('image/');
                          const isPDF = archivo.tipo_mime === 'application/pdf';
                          const fileUrl = archivo.url_archivo?.startsWith('http') 
                            ? archivo.url_archivo 
                            : `${process.env.REACT_APP_BACKEND_URL}${archivo.url_archivo}`;
                          const fileSize = archivo.tamano ? (archivo.tamano / 1024).toFixed(1) + ' KB' : '';
                          
                          return (
                            <div 
                              key={archivo.id} 
                              className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all"
                            >
                              <div className="flex-shrink-0">
                                {isImage ? (
                                  <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-100 border border-slate-200">
                                    <img 
                                      src={fileUrl} 
                                      alt={archivo.nombre_original}
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                ) : isPDF ? (
                                  <div className="w-12 h-12 rounded-lg bg-red-50 border border-red-200 flex items-center justify-center">
                                    <FileText className="w-6 h-6 text-red-500" />
                                  </div>
                                ) : (
                                  <div className="w-12 h-12 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center">
                                    <FileText className="w-6 h-6 text-slate-400" />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-800 truncate">
                                  {archivo.nombre_original}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {fileSize}
                                </p>
                              </div>
                              <div className="flex-shrink-0 flex gap-1">
                                {canPreviewFile(archivo.nombre_original) && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    onClick={() => handleFilePreview(fileUrl, archivo.nombre_original)}
                                    title="Previsualizar"
                                    data-testid={`btn-preview-archivo-${archivo.id}`}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  onClick={() => {
                                    const link = document.createElement('a');
                                    link.href = fileUrl;
                                    link.download = archivo.nombre_original;
                                    link.target = '_blank';
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                  }}
                                  title="Descargar archivo"
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* Gestión del Reclamo - Siempre visible, solo lectura cuando TERMINADO */}
                {(
                  <div ref={gestionRef} className="rounded-xl border-2 border-amber-200 bg-amber-50/50 overflow-hidden">
                    <div className="bg-amber-100/50 px-4 py-3 border-b border-amber-200">
                      <h3 className="font-heading font-semibold text-amber-900 flex items-center gap-2">
                        <Settings className="w-4 h-4 text-amber-600" />
                        Gestión del Reclamo
                      </h3>
                    </div>
                    <div className="p-3.5 flex flex-col gap-3 [&>*]:flex-none [&>span>*]:flex-none">
                      {/* Indicador SIREC - Solo lectura */}
                      {selectedReclamo.proyectos?.reportar_sirec && (
                        <div className="flex items-center gap-3 rounded-lg border-2 border-amber-300 bg-amber-100 p-3">
                          <FileWarning className="w-5 h-5 text-amber-700 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-amber-900">
                              Proyecto con reporte al SIREC
                            </p>
                            <p className="text-xs text-amber-700">
                              Este reclamo debe ser reportado al Sistema de Reclamos de INDECOPI
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Fila compacta: Estado / Responsable / Tipo Interno */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                        <div className="flex flex-col gap-1.5">
                          <Label className="text-xs font-semibold text-slate-700">Estado del Reclamo</Label>
                          <Select
                            value={selectedReclamo.estado}
                            onValueChange={(value) => setSelectedReclamo({ ...selectedReclamo, estado: value })}
                            disabled={updating || selectedReclamo.estado === 'TERMINADO' || (!hasRole('ADMINISTRADOR') && !hasRole('SUPERADMIN'))}
                          >
                            <SelectTrigger data-testid="estado-select" className="bg-white h-9">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ESTADOS.map((e) => (
                                <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <Label className="text-xs font-semibold text-slate-700">Responsable Asignado</Label>
                          <Select
                            value={selectedReclamo.responsable_id || ''}
                            onValueChange={(value) => setSelectedReclamo({ ...selectedReclamo, responsable_id: value })}
                            disabled={updating || hasRole('LEGAL') || selectedReclamo.estado === 'TERMINADO'}
                          >
                            <SelectTrigger data-testid="responsable-select" className="bg-white h-9">
                              <SelectValue placeholder="Sin asignar" />
                            </SelectTrigger>
                            <SelectContent>
                              {usuarios.map((u) => (
                                <SelectItem key={u.id} value={u.id}>{u.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <Label className="text-xs font-semibold text-slate-700">Tipo Interno</Label>
                          <Select
                            value={selectedReclamo.tipo_interno || tipoInternoDefault}
                            onValueChange={(value) => setSelectedReclamo({ ...selectedReclamo, tipo_interno: value })}
                            disabled={updating || hasRole('LEGAL') || selectedReclamo.estado === 'TERMINADO'}
                          >
                            <SelectTrigger data-testid="tipo-interno-select" className="bg-white h-9">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {tiposInternos.map((t) => (
                                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <Label className="text-xs font-semibold text-slate-700">Observaciones Internas</Label>
                        <Textarea
                          value={selectedReclamo.observaciones || ''}
                          onChange={(e) => setSelectedReclamo({ ...selectedReclamo, observaciones: e.target.value })}
                          placeholder="Agregar observaciones internas sobre el seguimiento del reclamo..."
                          rows={2}
                          className="bg-white text-sm"
                          disabled={hasRole('LEGAL') || selectedReclamo.estado === 'TERMINADO'}
                        />
                      </div>

                      {/* Switch Reportado al SIREC - Solo visible si el proyecto reporta a SIREC */}
                      {selectedReclamo.proyectos?.reportar_sirec && (
                        <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-3">
                          <div className="flex flex-col gap-0.5">
                            <Label className="text-sm font-medium text-slate-700">Reportado al SIREC</Label>
                            <p className="text-xs text-slate-500">
                              Marcar cuando el reclamo haya sido reportado a INDECOPI
                            </p>
                          </div>
                          <Switch
                            data-testid="reportado-sirec-switch"
                            checked={selectedReclamo.reportado_sirec || false}
                            onCheckedChange={(checked) => setSelectedReclamo({ ...selectedReclamo, reportado_sirec: checked })}
                            disabled={updating || hasRole('LEGAL') || selectedReclamo.estado === 'TERMINADO'}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Documentos de Respuesta Formal - Siempre visible, solo lectura cuando TERMINADO */}
                {(
                  <div className="rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                    <div
                      className="px-3.5 py-2.5 border-b border-slate-200 border-l-[3px]"
                      style={{
                        background: `linear-gradient(to right, color-mix(in srgb, ${themeColors.primario || '#475569'} 10%, transparent), color-mix(in srgb, ${themeColors.primario || '#475569'} 3%, transparent))`,
                        borderLeftColor: themeColors.primario || '#475569',
                      }}
                    >
                      <h3 className="font-heading font-bold text-slate-900 text-sm flex items-center gap-2">
                        <FileCheck className="w-4 h-4" style={{ color: themeColors.primario || '#475569' }} />
                        Documento de Respuesta Formal
                        {respuestas.length > 0 && (
                          <Badge variant="secondary" className="ml-2">{respuestas.length}</Badge>
                        )}
                      </h3>
                    </div>
                    <div className="p-3.5 flex flex-col gap-4 [&>*]:flex-none [&>span>*]:flex-none">
                      {/* Fecha de Comunicación de la Respuesta */}
                      <div className="flex flex-col gap-2">
                        <Label className="text-sm font-medium text-slate-700">Fecha de Comunicación de la Respuesta</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={`w-full justify-start text-left font-normal ${!selectedReclamo.fecha_comunicacion_respuesta && 'text-muted-foreground'}`}
                              disabled={hasRole('LEGAL') || selectedReclamo.estado === 'TERMINADO'}
                              data-testid="fecha-comunicacion-btn"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {selectedReclamo.fecha_comunicacion_respuesta 
                                ? format(new Date(selectedReclamo.fecha_comunicacion_respuesta), "dd 'de' MMMM yyyy", { locale: es })
                                : 'Seleccionar fecha'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={selectedReclamo.fecha_comunicacion_respuesta ? new Date(selectedReclamo.fecha_comunicacion_respuesta) : undefined}
                              onSelect={(date) => setSelectedReclamo({ 
                                ...selectedReclamo, 
                                fecha_comunicacion_respuesta: date ? date.toISOString() : null 
                              })}
                              locale={es}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>

                      {/* Botón de subir archivo - Solo si NO está terminado y NO es LEGAL */}
                      {!hasRole('LEGAL') && selectedReclamo.estado !== 'TERMINADO' && (
                        <div className="flex items-center gap-3 pt-4 mt-4 border-t border-slate-100">
                          <input
                            ref={respuestaInputRef}
                            type="file"
                            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                            onChange={handleUploadRespuesta}
                            className="hidden"
                            id="respuesta-upload"
                            data-testid="respuesta-upload-input"
                          />
                          <Button
                            variant="outline"
                            onClick={() => respuestaInputRef.current?.click()}
                            disabled={uploadingRespuesta}
                            data-testid="btn-adjuntar-respuesta"
                          >
                            {uploadingRespuesta ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                Subiendo...
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4 mr-2" />
                                Adjuntar Respuesta
                              </>
                            )}
                          </Button>
                          <span className="text-xs text-slate-500">PDF o Word, máximo 5MB</span>
                        </div>
                      )}

                      {/* Lista de documentos */}
                      {loadingRespuestas ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                        </div>
                      ) : respuestas.length > 0 ? (
                        <div className="space-y-2">
                          {respuestas.map((resp) => {
                            const isPDF = resp.nombre_archivo?.toLowerCase().endsWith('.pdf');
                            const fileUrl = resp.url_archivo?.startsWith('http') 
                              ? resp.url_archivo 
                              : `${process.env.REACT_APP_BACKEND_URL}${resp.url_archivo}`;
                            
                            return (
                              <div 
                                key={resp.id}
                                className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200 hover:border-slate-300 transition-all"
                                data-testid={`respuesta-doc-${resp.id}`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isPDF ? 'bg-red-50 border border-red-200' : 'bg-blue-50 border border-blue-200'}`}>
                                    <FileText className={`w-5 h-5 ${isPDF ? 'text-red-500' : 'text-blue-500'}`} />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-slate-800 truncate max-w-[200px]">
                                      {resp.nombre_archivo}
                                    </p>
                                    <p className="text-xs text-slate-500">
                                      Subido por {resp.usuarios?.nombre || 'Usuario'} • {new Date(resp.created_at).toLocaleDateString('es-PE')}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1">
                                  {canPreviewFile(resp.nombre_archivo) && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0"
                                      onClick={() => handleFilePreview(fileUrl, resp.nombre_archivo)}
                                      title="Previsualizar"
                                      data-testid={`btn-ver-respuesta-${resp.id}`}
                                    >
                                      <Eye className="w-4 h-4" />
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    onClick={() => {
                                      const link = document.createElement('a');
                                      link.href = fileUrl;
                                      link.download = resp.nombre_archivo;
                                      link.target = '_blank';
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }}
                                    title="Descargar documento"
                                    data-testid={`btn-descargar-respuesta-${resp.id}`}
                                  >
                                    <Download className="w-4 h-4" />
                                  </Button>
                                  {/* Botón eliminar solo si NO está terminado y NO es LEGAL */}
                                  {!hasRole('LEGAL') && selectedReclamo.estado !== 'TERMINADO' && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                      onClick={() => handleDeleteRespuesta(resp.id)}
                                      title="Eliminar documento"
                                      data-testid={`btn-eliminar-respuesta-${resp.id}`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500 text-center py-2">
                          No hay documentos de respuesta adjuntos
                        </p>
                      )}

                      {/* Indicador de envío previo */}
                      {selectedReclamo.respuesta_enviada_reclamante && (
                        <Alert className="border-green-200 bg-green-50 mt-3">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <AlertDescription className="text-green-800 text-sm">
                            <strong>Respuesta enviada por correo al reclamante</strong>
                            {selectedReclamo.fecha_envio_respuesta_reclamante && (
                              <span> el {format(new Date(selectedReclamo.fecha_envio_respuesta_reclamante), "dd/MM/yyyy 'a las' HH:mm", { locale: es })}</span>
                            )}
                          </AlertDescription>
                        </Alert>
                      )}

                      {/* Botón Enviar respuesta a reclamante.
                          Se oculta cuando "Terminar y notificar" está disponible
                          (mismas precondiciones), para evitar dos acciones que
                          compiten. Sigue disponible para reenvíos en reclamos
                          ya TERMINADOS o cuando SIREC bloquea el cierre. */}
                      {selectedReclamo.fecha_comunicacion_respuesta && 
                       respuestas.length > 0 &&
                       !hasRole('LEGAL') &&
                       (
                         selectedReclamo.estado === 'TERMINADO' ||
                         (selectedReclamo.proyectos?.reportar_sirec && !selectedReclamo.reportado_sirec)
                       ) &&
                       (hasRole('ADMINISTRADOR') || hasRole('SUPERADMIN') || 
                         ((hasRole('SUPERVISOR') || hasRole('USUARIO')) && user?.id === selectedReclamo.responsable_id)
                       ) && (
                        <div className="pt-3 mt-3 border-t border-slate-100">
                          <Button
                            onClick={async () => {
                              setPreviewMode('enviar');
                              setLoadingPreview(true);
                              setPreviewDialogOpen(true);
                              try {
                                const res = await api.get(`/reclamos/${selectedReclamo.id}/preview-respuesta-reclamante`);
                                setPreviewData(res.data);
                              } catch (err) {
                                toast.error('Error al generar preview del correo');
                                setPreviewDialogOpen(false);
                              } finally {
                                setLoadingPreview(false);
                              }
                            }}
                            variant="outline"
                            className="w-full text-blue-700 border-blue-300 hover:bg-blue-50"
                            disabled={enviandoRespuesta}
                            data-testid="btn-enviar-respuesta-reclamante"
                          >
                            <Mail className="w-4 h-4 mr-2" />
                            {selectedReclamo.respuesta_enviada_reclamante ? 'Reenviar respuesta a reclamante' : 'Enviar respuesta a reclamante'}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Reclamo Terminado - Info de cierre */}
                {selectedReclamo.estado === 'TERMINADO' && (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 overflow-hidden">
                    <div className="bg-emerald-100/50 px-4 py-3 border-b border-emerald-200">
                      <h3 className="font-heading font-semibold text-emerald-900 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-600" />
                        Reclamo Finalizado
                      </h3>
                    </div>
                    <div className="p-3.5">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Fecha de Cierre</p>
                          <p className="text-sm font-medium text-slate-800 mt-1">
                            {selectedReclamo.fecha_terminado 
                              ? format(new Date(selectedReclamo.fecha_terminado), "dd/MM/yyyy 'a las' HH:mm", { locale: es })
                              : '—'}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Atendido por</p>
                          <p className="text-sm font-medium text-slate-800 mt-1">
                            {selectedReclamo.usuarios?.nombre || '—'}
                          </p>
                        </div>
                      </div>
                      {selectedReclamo.observaciones && (
                        <div className="mt-4 pt-4 border-t border-emerald-200">
                          <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Observaciones</p>
                          <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{selectedReclamo.observaciones}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Metadata */}
                <div className="flex items-center justify-between text-xs text-slate-400 pt-2">
                  <div className="flex items-center gap-4">
                    <span>ID: {selectedReclamo.id?.substring(0, 8)}...</span>
                    {selectedReclamo.ip_address && <span>IP: {selectedReclamo.ip_address}</span>}
                  </div>
                  <span>
                    Última actualización: {format(new Date(selectedReclamo.updated_at || selectedReclamo.created_at), 'dd/MM/yyyy HH:mm')}
                  </span>
                </div>
              </div>

              <DialogFooter className="border-t pt-4 gap-2">
                {/* Botón Reenviar Correo */}
                {selectedReclamo.correo_electronico && selectedReclamo.estado !== 'TERMINADO' && !hasRole('LEGAL') && (
                  <Button
                    variant="outline"
                    onClick={async () => {
                      setReenviandoCorreo(true);
                      try {
                        const res = await api.post(`/reclamos/${selectedReclamo.id}/reenviar-correo`);
                        toast.success(res.data.message || 'Correo reenviado correctamente');
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Error al reenviar el correo');
                      } finally {
                        setReenviandoCorreo(false);
                      }
                    }}
                    disabled={reenviandoCorreo}
                    className="text-slate-700 border-slate-300 hover:bg-slate-50"
                    data-testid="btn-reenviar-correo"
                  >
                    {reenviandoCorreo ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Reenviando...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Reenviar Correo
                      </>
                    )}
                  </Button>
                )}
                <Button variant="outline" onClick={() => setDetailOpen(false)}>
                  Cerrar
                </Button>
                {/* Botón Terminar (y notificar): visible si tiene fecha_comunicacion_respuesta, archivo de respuesta,
                    SIREC OK, y permisos. Si el reclamante YA fue notificado, el botón actúa como simple
                    "Terminar" (sin preview ni reenvío del correo). */}
                {selectedReclamo.estado !== 'TERMINADO' && 
                 selectedReclamo.fecha_comunicacion_respuesta && 
                 respuestas.length > 0 &&
                 (!selectedReclamo.proyectos?.reportar_sirec || selectedReclamo.reportado_sirec) &&
                 (hasRole('ADMINISTRADOR') || hasRole('SUPERADMIN') || 
                   ((hasRole('SUPERVISOR') || hasRole('USUARIO')) && user?.id === selectedReclamo.responsable_id)
                 ) && (() => {
                  const yaNotificado = !!selectedReclamo.respuesta_enviada_reclamante;
                  const handleTerminarDirecto = async () => {
                    setUpdating(true);
                    try {
                      const res = await api.post(`/reclamos/${selectedReclamo.id}/terminar`, {
                        fecha_comunicacion_respuesta: selectedReclamo.fecha_comunicacion_respuesta,
                        reportado_sirec: selectedReclamo.reportado_sirec || false,
                        notificar_reclamante: false,
                      });
                      toast.success(res.data?.message || 'Reclamo terminado exitosamente');
                      fetchReclamos();
                      const updated = await api.get(`/reclamos/${selectedReclamo.id}`);
                      setSelectedReclamo(updated.data);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Error al terminar el reclamo');
                    } finally {
                      setUpdating(false);
                    }
                  };
                  const handleTerminarConPreview = async () => {
                    setPreviewMode('terminar_y_notificar');
                    setLoadingPreview(true);
                    setPreviewDialogOpen(true);
                    try {
                      const res = await api.get(`/reclamos/${selectedReclamo.id}/preview-respuesta-reclamante`);
                      setPreviewData(res.data);
                    } catch (err) {
                      toast.error('Error al generar preview del correo');
                      setPreviewDialogOpen(false);
                    } finally {
                      setLoadingPreview(false);
                    }
                  };
                  return (
                    <Button
                      onClick={yaNotificado ? handleTerminarDirecto : handleTerminarConPreview}
                      disabled={updating || enviandoRespuesta}
                      className="bg-green-600 hover:bg-green-700 text-white min-w-[180px]"
                      data-testid="btn-terminar-reclamo"
                      title={yaNotificado
                        ? 'El reclamante ya fue notificado. Al confirmar, el reclamo se marcará como TERMINADO.'
                        : undefined}
                    >
                      {(updating || (enviandoRespuesta && previewMode === 'terminar_y_notificar')) ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          {yaNotificado ? 'Terminando...' : 'Procesando...'}
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          {yaNotificado ? 'Terminar' : 'Terminar y notificar'}
                        </>
                      )}
                    </Button>
                  );
                })()}
                {/* Mostrar botón Guardar si: NO está terminado, O si es ADMIN y puede cambiar estado */}
                {!hasRole('LEGAL') && selectedReclamo.estado !== 'TERMINADO' && (
                  <Button 
                    onClick={async () => {
                      setUpdating(true);
                      try {
                        const updatePayload = {
                          estado: selectedReclamo.estado,
                          responsable_id: selectedReclamo.responsable_id || null,
                          observaciones: selectedReclamo.observaciones || '',
                          fecha_comunicacion_respuesta: selectedReclamo.fecha_comunicacion_respuesta || null,
                          tipo_interno: selectedReclamo.tipo_interno || tipoInternoDefault,
                        };
                        
                        // Incluir reportado_sirec solo si el proyecto reporta a SIREC
                        if (selectedReclamo.proyectos?.reportar_sirec) {
                          updatePayload.reportado_sirec = selectedReclamo.reportado_sirec || false;
                        }
                        
                        await api.put(`/reclamos/${selectedReclamo.id}`, updatePayload);
                        toast.success('Reclamo actualizado correctamente');
                        fetchReclamos();
                        
                        // Re-obtener el reclamo actualizado para refrescar el estado local
                        const response = await api.get(`/reclamos/${selectedReclamo.id}`);
                        setSelectedReclamo(response.data);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Error al actualizar el reclamo');
                      } finally {
                        setUpdating(false);
                      }
                    }}
                    disabled={updating}
                    className="min-w-[140px]"
                    data-testid="btn-guardar-reclamo"
                  >
                    {updating ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Guardar Cambios
                      </>
                    )}
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog de Preview de Correo al Reclamante */}
      <Dialog open={previewDialogOpen} onOpenChange={(open) => {
        if (!enviandoRespuesta) {
          setPreviewDialogOpen(open);
          if (!open) setPreviewMode('enviar');
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              {previewMode === 'terminar_y_notificar' ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <Mail className="w-5 h-5" />
              )}
              {previewMode === 'terminar_y_notificar'
                ? 'Terminar reclamo y notificar al reclamante'
                : 'Preview del correo al reclamante'}
            </DialogTitle>
            <DialogDescription>
              {previewMode === 'terminar_y_notificar'
                ? 'Revise el correo. Al confirmar, se enviará al reclamante y el reclamo quedará como TERMINADO. Si el envío falla, el reclamo no será terminado.'
                : 'Revise el contenido del correo antes de enviarlo.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto min-h-0">
            {loadingPreview ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : previewData && (
              <div className="space-y-4">
                <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm bg-slate-50 p-3 rounded-lg">
                  <span className="font-medium text-slate-600">Para:</span>
                  <span data-testid="preview-destinatario">{previewData.destinatario}</span>
                  <span className="font-medium text-slate-600">Asunto:</span>
                  <span data-testid="preview-asunto">{previewData.asunto}</span>
                  <span className="font-medium text-slate-600">Adjuntos:</span>
                  <span data-testid="preview-adjuntos">{previewData.archivos?.join(', ')}</span>
                </div>

                <div className="border rounded-lg overflow-hidden">
                  <iframe
                    srcDoc={previewData.html}
                    title="Preview del correo"
                    className="w-full h-[400px] border-0"
                    sandbox=""
                    data-testid="preview-iframe"
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex-shrink-0 gap-2 border-t pt-4">
            <Button variant="outline" onClick={() => setPreviewDialogOpen(false)} disabled={enviandoRespuesta}>
              Cancelar
            </Button>
            <Button
              onClick={async () => {
                setEnviandoRespuesta(true);
                try {
                  if (previewMode === 'terminar_y_notificar') {
                    // Envía correo + termina (transaccional en backend)
                    const res = await api.post(`/reclamos/${selectedReclamo.id}/terminar`, {
                      fecha_comunicacion_respuesta: selectedReclamo.fecha_comunicacion_respuesta,
                      reportado_sirec: selectedReclamo.reportado_sirec || false,
                      notificar_reclamante: true,
                    });
                    toast.success(res.data.message || 'Reclamo terminado y notificado');
                    setPreviewDialogOpen(false);
                    fetchReclamos();
                    const updated = await api.get(`/reclamos/${selectedReclamo.id}`);
                    setSelectedReclamo(updated.data);
                  } else {
                    const res = await api.post(`/reclamos/${selectedReclamo.id}/enviar-respuesta-reclamante`);
                    toast.success(res.data.message);
                    setPreviewDialogOpen(false);
                    const updated = await api.get(`/reclamos/${selectedReclamo.id}`);
                    setSelectedReclamo(updated.data);
                  }
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Error al procesar la operación');
                } finally {
                  setEnviandoRespuesta(false);
                }
              }}
              disabled={enviandoRespuesta || loadingPreview}
              className={previewMode === 'terminar_y_notificar'
                ? 'bg-green-600 hover:bg-green-700 min-w-[200px]'
                : 'bg-blue-600 hover:bg-blue-700 min-w-[200px]'}
              data-testid="btn-confirmar-envio-respuesta"
            >
              {enviandoRespuesta ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  {previewMode === 'terminar_y_notificar' ? 'Terminando y enviando...' : 'Enviando...'}
                </>
              ) : (
                <>
                  {previewMode === 'terminar_y_notificar' ? (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Confirmar y terminar
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Enviar Correo
                    </>
                  )}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Previsualización de Archivos */}
      <Dialog open={filePreviewModal.open} onOpenChange={(open) => {
        if (!open && filePreviewModal.url) {
          window.URL.revokeObjectURL(filePreviewModal.url);
        }
        setFilePreviewModal(prev => ({ ...prev, open }));
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm">
              <Eye className="w-4 h-4" />
              {filePreviewModal.nombre}
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center min-h-[300px] max-h-[70vh] overflow-auto">
            {['jpg', 'jpeg', 'png'].includes(filePreviewModal.tipo) && (
              <img
                src={filePreviewModal.url}
                alt={filePreviewModal.nombre}
                className="max-w-full max-h-[65vh] object-contain rounded"
                data-testid="file-preview-image"
              />
            )}
            {filePreviewModal.tipo === 'pdf' && (
              <iframe
                src={filePreviewModal.url}
                title={filePreviewModal.nombre}
                className="w-full h-[65vh] rounded border"
                data-testid="file-preview-pdf"
              />
            )}
            {['mp4', 'mov'].includes(filePreviewModal.tipo) && (
              <video
                src={filePreviewModal.url}
                controls
                className="max-w-full max-h-[65vh] rounded"
                data-testid="file-preview-video"
              >
                Tu navegador no soporta la reproducción de video.
              </video>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReclamosPage;
